import {
    type DashboardModel,
    type IStage,
    type MovIntegrationMDSModel,
    type NcoJSON,
    platformsDashboard,
} from '@metservicedev/platforms-services-interface/src';
import qs from 'qs';
import React, { useState, useRef, useMemo } from 'react';
import './App.css';
import { useEffect } from 'react';
import { type Layouts, Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import useSize from '@react-hook/size';

import type { WidgetblockResponseModel } from '@metservicedev/platforms-services-interface/src/services/platformsWidgetblock/WidgetblockRequestModels';
import { dashboardRowHeight } from '@mono/mov-constants';
import type { Capability } from '@mono/types/src/mds';
import { usePlatStageContext } from '@mono/use-plat-stage';
import { useUserTracking } from '@mono/user-tracking';

import { WidgetblockRenderer } from './WidgetblockRenderer';

import { useMDSContext } from '@metservice/use-mds';
import { filterVariables } from '@metservicedev/ncojs';
import type { IDatability } from '@metservicedev/platforms-services-interface/src/model/Widgetblock';
import { mds } from '@metservicedev/platforms-services-interface/src/services/met-integration';
import { DateTime, Duration } from 'luxon';
import styled from 'styled-components';

const TopDiv = styled.div`
    height: 100%;
    position: relative;
    overflow-y: scroll;
    ::-webkit-scrollbar {
        display: none;
        -ms-overflow-style: none;
    }
    overflow-x: hidden;
`;

const stripExtraData = (cfjson: NcoJSON.NcoJSONRAW, databilities: IDatability[], capabilities: Capability[]) => {
    const capabilitiesForCfjson = capabilities.filter((capability) => databilities.some((d) => d.capabilityId === capability.id));
    if (capabilitiesForCfjson.length < 1) {
        console.log('could not find any capabilities - not removing any variables');
        return cfjson;
    }

    const variableNames = [...capabilitiesForCfjson.flatMap((c) => Object.keys(c.variables ?? {})), ...Object.keys(cfjson.dimensions)];
    return filterVariables(cfjson, variableNames) as NcoJSON.NcoJSONRAW; // This cast is needed until we make ncojs the source of truth for the NcoJSON type
};

const addTimeRangeBuffer = (timeRange: string) => {
    const [start, end] = timeRange.split('--');
    return `${Duration.fromISO(start)
        .minus(Duration.fromObject({ hours: 12 }))
        .toISO()}--${Duration.fromISO(end)
        .plus(Duration.fromObject({ hours: 14 }))
        .toISO()}`;
};

const durationToTimeRange = (timeRange: string) => {
    const [fromTime, toTime] = timeRange.split('--').map((d, i) => {
        return DateTime.utc().plus(Duration.fromISO(d)).plus({ hour: i }).startOf('hour').toISO({ suppressMilliseconds: true });
    });
    return `${fromTime}--${toTime}`;
};

const App = () => {
    const { capabilities } = useMDSContext();
    const [errorMsg, setErrorMsg] = useState();
    const { stage } = usePlatStageContext();
    const { track, isUserTrackingLoaded } = useUserTracking();

    const ref = useRef<HTMLDivElement>(null);
    const [width] = useSize(ref);
    const [interval, setInterval] = useState(30 * 1000); // 30 seconds

    const [state, setState] = useState<{
        dashboard: DashboardModel | null;
        getCfjsonsResponse: MovIntegrationMDSModel.GetCfjsonsResponse | null;
        date: string;
        widgetblocks: WidgetblockResponseModel[];
        datasets: MovIntegrationMDSModel.IDataset[];
    }>({
        dashboard: null,
        getCfjsonsResponse: null,
        datasets: [],
        date: new Date().toISOString(),
        widgetblocks: [],
    });

    const query = async ({ stage, id, config, data, jwt }: { stage: IStage; id?: string; config?: string; data?: string; jwt: string | null }) => {
        try {
            console.time('Dashboard');
            if (!id && !config && !data && !jwt) {
                return;
            }
            if (config && data && jwt) {
                const dashboard: DashboardModel = JSON.parse(config);
                setState((prev) => ({ ...prev, dashboard }));

                const parsedDatabilities: IDatability[] = JSON.parse(data);
                const databilityIndexes = parsedDatabilities.map((d) =>
                    [d.datasetId, JSON.stringify(d.space), d.timeRange, d.capabilityId === 'specden'].join('₿')
                );
                const widgetblockIds: string[] = dashboard.widgetblockItems.map(({ widgetblockId }) => widgetblockId).filter((a) => a !== null);

                fetch(
                    `https://5jwe7rofob.execute-api.us-east-1.amazonaws.com/test/v0/rest/widgetblock/get/token?${qs.stringify({ widgetblockIds }, { arrayFormat: 'comma' })}`,
                    {
                        headers: {
                            'x-cookie-token': jwt,
                        },
                    }
                ).then((response) => {
                    response.json().then((data) => {
                        setState((prev) => ({
                            ...prev,
                            widgetblocks: data,
                        }));
                    });
                });

                const datasetIds: string[] = [...new Set(parsedDatabilities.map((d) => d.datasetId).filter((d) => d !== undefined))];

                const uniqueDatabilityIndexes = [...new Set(databilityIndexes)];

                const requestVariables = uniqueDatabilityIndexes.map((databilityIndex) => {
                    const [datasetId, space, timeRange, isSpectra] = databilityIndex.split('₿');
                    const stack = datasetId.includes('gfs') ? 'gfs' : 'ecmwf';
                    const { coordinates } = JSON.parse(space);
                    const [lon, lat] = coordinates;
                    const timeRangeWithBuffer = durationToTimeRange(addTimeRangeBuffer(timeRange ?? ''));

                    if (datasetId.includes('forecastapi_atmos')) {
                        return {
                            datasetId,
                            stack,
                            lat,
                            lon,
                            timeRange,
                            timeRangeWithBuffer,
                            type: 'forecast',
                        };
                    }
                    if (datasetId.endsWith('_v2.0')) {
                        return {
                            datasetId,
                            stack,
                            lat,
                            lon,
                            timeRange,
                            timeRangeWithBuffer,
                            type: 'regrid',
                        };
                    }
                    return {
                        datasetId,
                        stack,
                        lat,
                        lon,
                        timeRange,
                        timeRangeWithBuffer,
                        type: 'spectra',
                        isSpectra,
                    };
                });

                fetch(`${mds[stage]}/v1/queries/datasets/find?id=${[...new Set(datasetIds)].join(',')}`, {
                    headers: {
                        'x-cookie-token': jwt,
                    },
                }).then((response) => {
                    response.json().then((data) => {
                        const findDatasetsResponse = data ?? {
                            'basic-datasets': [],
                            'datasource-datasets': [],
                        };
                        const datasets = [...findDatasetsResponse['basic-datasets'], ...findDatasetsResponse['datasource-datasets']];
                        setState((prev) => ({ ...prev, datasets }));
                    });
                });

                requestVariables.forEach((request) => {
                    const { datasetId, stack, lat, lon, timeRange, timeRangeWithBuffer, type, isSpectra } = request;

                    if (type === 'forecast') {
                        fetch(
                            `https://d2z3xm23sp4nm4.cloudfront.net/point/time?vars=wind_from_direction_at_10m_above_ground_level%2Cwind_speed_at_10m_above_ground_level&lats=${lat}&lons=${lon}&stack=${stack}&limit=9999999&time=${timeRangeWithBuffer}`,
                            {
                                headers: {
                                    'x-cookie-token': jwt,
                                },
                            }
                        ).then((response) => {
                            response.json().then((data) => {
                                const cfjsonResponse = {
                                    cfjson: stripExtraData(data, parsedDatabilities, capabilities),
                                    error: data.error,
                                    databilityIndex: `${datasetId}₿{\"type\":\"Point\",\"coordinates\":[${lon},${lat}]}₿₿${timeRange}₿timeseries/point2d`,
                                };
                                setState((prev) => ({
                                    ...prev,
                                    getCfjsonsResponse: {
                                        data: [...(prev.getCfjsonsResponse?.data ?? []), cfjsonResponse],
                                    },
                                }));
                            });
                        });
                        return;
                    }
                    if (type === 'regrid') {
                        fetch(
                            `https://d320ob6gk50vue.cloudfront.net/timeseries/${datasetId}/points?vars=sea_surface_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_below_8s_period_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_above_8s_period_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_wave_significant_height%2Csea_surface_below_8s_period_wave_significant_height%2Csea_surface_above_8s_period_wave_significant_height%2Csea_surface_wave_period_at_variance_spectral_density_maximum&lats=${lat}&lons=${lon}&limit=9999999&time=${timeRangeWithBuffer}`,
                            {
                                headers: {
                                    'x-cookie-token': jwt,
                                },
                            }
                        ).then((response) => {
                            response.json().then((data) => {
                                const cfjsonResponse = {
                                    cfjson: stripExtraData(data, parsedDatabilities, capabilities),
                                    error: data.error,
                                    databilityIndex: `${datasetId}₿{\"type\":\"Point\",\"coordinates\":[${lon},${lat}]}₿₿${timeRange}₿timeseries/point2d`,
                                };
                                setState((prev) => ({
                                    ...prev,
                                    getCfjsonsResponse: {
                                        data: [...(prev.getCfjsonsResponse?.data ?? []), cfjsonResponse],
                                    },
                                }));
                            });
                        });
                        return;
                    }
                    if (type === 'spectra') {
                        if (isSpectra === 'true') {
                            fetch(`https://d3opub5lmx5o2v.cloudfront.net/spectra/${datasetId}?limit=9999999&time=${timeRangeWithBuffer}`, {
                                headers: {
                                    'x-cookie-token': jwt,
                                },
                            }).then((response) => {
                                response.json().then((data) => {
                                    const cfjsonResponse = {
                                        cfjson: stripExtraData(data, parsedDatabilities, capabilities),
                                        error: data.error,
                                        databilityIndex: `${datasetId}₿{\"type\":\"Point\",\"coordinates\":[${lon},${lat}]}₿₿${timeRange}₿timeseries/point2d`,
                                    };
                                    setState((prev) => ({
                                        ...prev,
                                        getCfjsonsResponse: {
                                            data: [...(prev.getCfjsonsResponse?.data ?? []), cfjsonResponse],
                                        },
                                    }));
                                });
                            });
                            console.timeEnd('Dashboard');
                            return;
                        }
                        fetch(
                            `https://d3opub5lmx5o2v.cloudfront.net/spectra/${datasetId}/parameters?vars=sea_surface_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_below_8s_period_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_above_8s_period_wave_from_direction_at_variance_spectral_density_maximum%2Csea_surface_wave_significant_height%2Csea_surface_below_8s_period_wave_significant_height%2Csea_surface_above_8s_period_wave_significant_height%2Csea_surface_wave_period_at_variance_spectral_density_maximum&partitioning_method=8s_split&lats=${lat}&lons=${lon}&limit=9999999&time=${timeRangeWithBuffer}`,
                            {
                                headers: {
                                    'x-cookie-token': jwt,
                                },
                            }
                        ).then((response) => {
                            response.json().then((data) => {
                                const cfjsonResponse = {
                                    cfjson: stripExtraData(data, parsedDatabilities, capabilities),
                                    error: data.error,
                                    databilityIndex: `${datasetId}₿{\"type\":\"Point\",\"coordinates\":[${lon},${lat}]}₿₿${timeRange}₿timeseries/point2d`,
                                };
                                setState((prev) => ({
                                    ...prev,
                                    getCfjsonsResponse: {
                                        data: [...(prev.getCfjsonsResponse?.data ?? []), cfjsonResponse],
                                    },
                                }));
                            });
                        });
                        return;
                    }
                    console.error(`Unknown request type: ${type}`, request);
                    return;
                });
            }

            if (!id) {
                return;
            }
        } catch (e) {
            console.error(e);
        } finally {
            document.querySelector('.loading')?.remove();
            setTimeout(() => query({ stage, id, config, data, jwt }), 60_000 * 20); // Refetch every 20 mins
        }
    };

    useEffect(() => {
        if (!isUserTrackingLoaded) return;
        track('dashboard_app', { action: 'loaded', action_details: [window.location.search.split('=')[1]] });
        return;
    }, [isUserTrackingLoaded, stage]);

    const memoizedLayouts = useMemo(() => {
        if (!state.dashboard) {
            return [] as unknown as Layouts;
        }

        return Object.fromEntries(
            Object.entries(state.dashboard.layouts).map(([key, layout]) => {
                return [key, layout.map((l) => ({ x: l.x * 10, y: l.y * 10, h: l.h * 10, w: l.w * 10, i: l.layoutItemId }))];
            })
        );
    }, [state.dashboard?.layouts]);

    const patientDataQuery = async (dashboardDataArgs: { stage: IStage; id: string }) => {
        try {
            const response = await platformsDashboard.http.getData(dashboardDataArgs);

            const {
                data: { dashboard, getCfjsons, widgetblocks },
            } = response;

            const findDatasetsResponse = response.data.findDatasetsResponse ?? {
                'basic-datasets': [],
                'datasource-datasets': [],
            };
            const datasets = [...findDatasetsResponse['basic-datasets'], ...findDatasetsResponse['datasource-datasets']];

            const shortestTimeInterval = getCfjsons?.data.reduce(
                (acc, { cfjson }) => {
                    if (!cfjson) return acc;
                    const timePoints = cfjson.dimensions.time;
                    const firstTimePoint = cfjson.variables.time.data[0];
                    const lastTimePoint = cfjson.variables.time.data[timePoints - 1];

                    const firstTimePointDate = DateTime.fromISO(firstTimePoint);
                    const lastTimePointDate = DateTime.fromISO(lastTimePoint);
                    const diff = lastTimePointDate.diff(firstTimePointDate).as('milliseconds');
                    const timePointInterval = diff / timePoints;

                    return Math.min(acc, Math.round(timePointInterval / 2));
                },
                dashboard.dashboardQueryInterval ?? 30 * 60 * 1000
            ); //30 minutes

            setInterval(shortestTimeInterval);

            setState({
                dashboard,
                getCfjsonsResponse: getCfjsons,
                widgetblocks,
                date: new Date().toISOString(),
                datasets,
            });
            setErrorMsg(undefined);
        } catch (e) {
            console.log({ e });
            setErrorMsg(e.message);
            throw e;
        } finally {
            document.querySelector('.loading')?.remove();
            setTimeout(() => patientDataQuery(dashboardDataArgs), interval);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        const config = params.get('config');
        const data = params.get('data');
        const jwt = params.get('jwt');

        if (config && data) {
            if (!capabilities || capabilities.length === 0) return;

            const dashboardDataArgs = {
                stage,
                config,
                data,
                jwt,
            };
            query(dashboardDataArgs);
            return;
        }

        const dashboardId = params.get('id');

        if (!dashboardId) {
            console.warn('no dashboardId');
            return;
        }

        const dashboardDataArgs = {
            stage,
            id: dashboardId,
        };

        patientDataQuery(dashboardDataArgs);
    }, [capabilities]);

    const widthToPadding = (width: number) => {
        if (width >= 2560) {
            return 8;
        }
        if (width >= 1920) {
            return 6;
        }
        if (width >= 1280) {
            return 4;
        }
        if (width >= 640) {
            return 2;
        }
        return 1;
    };

    return (
        <TopDiv ref={ref} className='top div' style={{ backgroundColor: state.dashboard?.theme === 'dark' ? '#31343A' : '#FFF' }}>
            {errorMsg && (
                <div style={{ width: '100%', height: window.innerHeight, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>Failed to load Dashboard</div>
                </div>
            )}
            {state.dashboard && state.getCfjsonsResponse && (
                <ResponsiveGridLayout
                    width={width}
                    layouts={memoizedLayouts}
                    breakpoints={{ lg: 2560 - 1, md: 1920 - 1, sm: 1280 - 1, xs: 640 - 1 }}
                    cols={{ lg: 80, md: 40, sm: 30, xs: 20 }}
                    isDraggable={false}
                    isResizable={false}
                    rowHeight={dashboardRowHeight}
                    containerPadding={[0, 0]}
                    margin={[0, 0]}
                >
                    {state.dashboard.widgetblockItems.map(({ widgetblockId, layoutItemId }) => {
                        const widgetblock = state.widgetblocks.find((w) => w.id === widgetblockId);
                        return (
                            <div key={layoutItemId} style={{ padding: widthToPadding(width) }}>
                                {widgetblock && state.getCfjsonsResponse && (
                                    <WidgetblockRenderer
                                        widgetblock={{ ...widgetblock, timezone: state.dashboard?.timezone || 'UTC', theme: state.dashboard?.theme }}
                                        getCfjsonsResponse={state.getCfjsonsResponse}
                                        datasets={state.datasets}
                                        date={new Date().toISOString()}
                                        updateInterval={interval}
                                    />
                                )}
                                {!widgetblock && <div style={{ width: '100%', height: '100%', border: '1px solid black', borderStyle: 'dashed' }} />}
                            </div>
                        );
                    })}
                </ResponsiveGridLayout>
            )}
        </TopDiv>
    );
};

export default App;
